import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Button,
    TextField,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormHelperText,
    InputLabel,
    FormControl,
    Link,
    Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import "./SingleProduct.css";
import { ASSETS_BASE_URL, EXTERNAL_2D_ENABLED, SERVER_BASE_URL, RSPL_LIST, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../config";
import { useAuth } from "../../context/authContext";
import Swal from "sweetalert2";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PageNotFound from "../PageNotFound/PageNotFound";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { ALERT_MESSAGES, BUTTON, LABELS, MENU_ITEMS, ROUTES, UI_TEXT, UI_TEXT_HELPERS } from "../../constants";
import { useTranslation } from "react-i18next";
import formatCurrency from "../../utilities/currency";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { getMachinesByIds, getSingleProduct } from "../../services/breadCrumbsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getCartRecommendations } from "../../services/cartService";
import { getRecommendationsByUserAndSite } from "../../services/recommendationsService";
import { addProductsToRecommendationList } from "../../services/rsplService";
import { createRecommendation } from "../../services/recommendationsService";
import { saveProductForLater, saveRequestList } from "../../services/productCardService";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import { toast } from "../../context/ToastContext";
import BomPathView from "../../components/BomViewer/BomPathView";
import { indexBomTree, buildPathTreeFromProductId } from "../../utilities/bomUtils";
import MultipleModelViewer from "../../components/MultipleModelViewer/MultipleModelViewer";
import {
    countAvailableModelsFromModelData,
    extractFilename,
    selectBestModelFromModelData,
} from "../../utilities/selectBestModel";
import { getSavedForLaterBySiteId, removeFromSavedForLater } from "../../services/savedForLetterService";
import { useOptions } from "../../context/optionContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";

const formatInlineLabel = (label) => `${String(label || "").replace(/[:\s]+$/g, "")}:`;

const SingleProduct = ({
    machineID: machineIdProp,
    productID: productIdProp,
    isModalView = false,
}) => {
    const { optionKeys } = useOptions();
    const resolvedRsplList = useMemo(
        () => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
        [optionKeys?.rsplList]
    );
    const rsplMapping = useMemo(
        () => buildRsplCodeToLabelMap(resolvedRsplList),
        [resolvedRsplList]
    );
    const params = useParams();
    const machineID = machineIdProp ?? params?.machineID;
    const productID = productIdProp ?? params?.productID;
    const { addToCart, cart, cartItems } = useCart(); // Added cartItems
    const { selectedSite, siteData, selectedSiteCurrency, currencySymbol } = useSite();
    const [price, setPrice] = useState(0);
    const [product, setProduct] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
   
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSFL, setIsLoadingSFL] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSuccessSFL, setIsSuccessSFL] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState("");
    const [newListName, setNewListName] = useState("");
    const [newListDescription, setNewListDescription] = useState("");
    const [newListError, setNewListError] = useState("");
    const [selectListError, setSelectListError] = useState("");
    const [listCreationLoading, setListCreationLoading] = useState(false);
    const [hasMultipleModels, setHasMultipleModels] = useState(false);
    const [machineModelData, setMachineModelData] = useState({});
    const [machine_name, setmachine_name] = useState([]);
    const [machine_number, setmachine_number] = useState([]);
    const [m3DButtonLoading, setM3DButtonLoading] = useState(false);
    const [modelModalOpen, setModelModalOpen] = useState(false);
    const [orderCount, setOrderCount] = useState(10);
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [recommendationsLoading, setRecommendationsLoading] = useState(false);
    const [orderIds, setOrderIds] = useState([]);
    const [bomTree, setBomTree] = useState([]);
    const [bomPathTree, setBomPathTree] = useState([]);
    const [bomPathLoading, setBomPathLoading] = useState(false);
    const [bomPathError, setBomPathError] = useState(false);
    const { user, hasPermission } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { stockImageUrl } = useBrandingAssets();
    const maxQtyAllowed = useMemo(() => {
    const parsed = parseInt(optionKeys?.maxOrderQty, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 999;
    }, [optionKeys]);
    const MAX_QTY = maxQtyAllowed;
    const { flatNodes: bomFlatNodes, nodeMap: bomNodeMap, productIdToNodeId } = useMemo(
        () => indexBomTree(bomTree),
        [bomTree]
    );
    const { selectedSiteData } = useSite();
    const triggerToast = (message, severity = "success") => {
        if (!message) return;
        toast.trigger({ message, severity });
    };
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        const getMachinesSavedForLater = async () => {
        try {
        const response = await getSavedForLaterBySiteId(selectedSite);
        if (response?.status && Array.isArray(response.data)) {
        const matchFound = response.data.some(
        (item) =>
        Number(item.machine_id) === Number(machineID) &&
        Number(item.product_id) === Number(productID)
        );
        setIsSuccessSFL(matchFound);
        }
        } catch (error) {
        console.error("Error fetching saved for later:", error);
        }
        };
        getMachinesSavedForLater();
    }, [machineID, productID, selectedSite]);
    const canAddToList = hasPermission("recommendations_management.add_parts")
    const canCreateList = hasPermission("recommendations_management.create_list")
    const partDescription = product?.product_description?.trim() || "";

    const productImage = useMemo(() => {
        const raw = product?.asset_data?.images?.[0];
        if (raw) {
            if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
            const path = raw.startsWith("/") ? raw : `/${raw}`;
            return `${ASSETS_BASE_URL}${path}`;
        }
        return `${ASSETS_BASE_URL}/ProductImages/TMB/${product?.product_number}.JPG`;
    }, [product]);

    const bestModel = useMemo(
        () => selectBestModelFromModelData(machineModelData),
        [machineModelData]
    );


    // CORRECTED FILTER FUNCTION - This fixes the FBT filtering issue
    const filterRecommendationsNotInCart = (recommendations, cartItems) => {
        if (!cartItems || cartItems.length === 0) return recommendations;

        // Get product IDs from cart items based on your actual cart structure
        const cartProductIds = cartItems
            .filter(item => item.Site === selectedSite) // Only current site items
            .map(item => item?.ProductID || item?.product_id)
            .filter(Boolean)
            .map((id) => String(id));

        const filtered = recommendations.filter(product =>
            !cartProductIds.includes(String(product.product_id))
        );
        return filtered;
    };

    useEffect(() => {
        if (!productID || !machineID || !selectedSite) {
            console.log(productID, machineID, selectedSite);
            return;
        }

        const fetchAllData = async () => {
            try {
                const reqBody = {
                    machine_id: machineID,
                    product_id: productID,
                    site_number: selectedSite
                };

                // Start both requests in parallel
                const productPromise = getSingleProduct(reqBody);
                console.log("Inside Singleproduct fetchall data getsingleproduct response",productPromise);
                // Create recommendations request promise but don't await it yet
                setRecommendationsLoading(true);
                const recommendationsPromise = getCartRecommendations([productID],selectedSite);

                // Wait for product data first (needed for UI)
                const productResponse = await productPromise;

                if (productResponse.status && productResponse.data?.data.length > 0) {
                    console.log(productResponse.data);
                    setProduct(productResponse.data.data[0]);

                    if (productResponse.data.order_data) {
                        const filteredOrders = productResponse.data.orders
                            .filter((order) => order.customer_site_number === selectedSite)
                            .map((order) => order.order_id);
                        setOrderCount(filteredOrders.length);
                        setOrderIds(filteredOrders);
                    } else {
                        setOrderCount(0);
                        setOrderIds([]);
                    }
                    setLoading(false);
                    setError(null);

                    // Now handle recommendations in parallel with initial filtering
                    try {
                        const recommendationsResponse = await recommendationsPromise;
                        console.log("RC Response: ", recommendationsResponse)
                        let recommendations = Array.isArray(recommendationsResponse?.data)
                            ? recommendationsResponse.data
                            : [];
                        
                        // Apply initial filter if cart exists
                        if (cartItems && cartItems.length > 0) {
                            recommendations = filterRecommendationsNotInCart(recommendations, cartItems);
                        }
                        
                        setRecommendedProducts(recommendations);
                    } catch (recError) {
                        console.error("Failed to fetch recommendations:", recError);
                        setRecommendedProducts([]);
                    }
                } else {
                    setError(t('GENERAL.FAILED_TO_FETCH_DATA_NO_PRODUCT_FOUND'));
                    setOrderCount(0);
                    setLoading(false);
                }
            } catch (err) {
                setError(t('GENERAL.FAILED_TO_FETCH_DATA'));
                setOrderCount(0);
                setLoading(false);
            } finally {
                setRecommendationsLoading(false);
            }
        };

        fetchAllData();
    }, [productID, machineID, selectedSite, cartItems]); // Added cartItems to dependencies

    useEffect(() => {
        if (!machineID || !selectedSite) {
            setBomTree([]);
            return;
        }
        let ignore = false;
        const fetchBomData = async () => {
            setBomPathLoading(true);
            setBomPathError(false);
            try {
                const response = await getProductsByMachineId({
                    machine_id: machineID,
                    site_number: selectedSite,
                });
                if (ignore) return;
                if (response.status) {
                    const { bomTree } = normalizeMachineProductsResponse(response);
                    setBomTree(bomTree || []);
                } else {
                    setBomTree([]);
                    setBomPathError(true);
                }
            } catch (error) {
                if (!ignore) {
                    setBomTree([]);
                    setBomPathError(true);
                }
            } finally {
                if (!ignore) {
                    setBomPathLoading(false);
                }
            }
        };
        fetchBomData();
        return () => {
            ignore = true;
        };
    }, [machineID, selectedSite]);

    useEffect(() => {
        if (!productID) {
            setBomPathTree([]);
            return;
        }
        const pathTree = buildPathTreeFromProductId(
            productID,
            bomFlatNodes,
            bomNodeMap,
            productIdToNodeId
        );
        setBomPathTree(pathTree);
    }, [productID, bomFlatNodes, bomNodeMap, productIdToNodeId]);

    useEffect(() => {
        const fetchMachineDetails = async () => {
            try {
                const reqBody = {
                    machines: [machineID],
                };

                const response = await getMachinesByIds(reqBody);

                if (response.status) {
                    const machineData = response.data[0];
                    setmachine_name(machineData.machine_name);
                    setmachine_number(machineData.machine_number);
                    const rawModelData = machineData.model_data;
                    let parsedModelData = {};
                    if (rawModelData && typeof rawModelData === "string") {
                        try {
                            const parsed = JSON.parse(rawModelData);
                            parsedModelData = parsed && typeof parsed === "object" ? parsed : {};
                        } catch {
                            parsedModelData = {};
                        }
                    } else if (rawModelData && typeof rawModelData === "object") {
                        parsedModelData = rawModelData;
                    }
                    setMachineModelData(parsedModelData);
                } else {
                    setError("Failed to fetch data: No success flag.");
                }
            } catch (err) {
                setError("Failed to fetch data: " + err.message);
            }
        };

        fetchMachineDetails();
    }, [machineID]);

    // Determine model availability based on cache (machineModelData)
    useEffect(() => {
        try {
            const md = machineModelData || {};
            const modelCount = countAvailableModelsFromModelData(md);
            setHasMultipleModels(modelCount > 1);
        } catch (e) {
            setHasMultipleModels(false);
        }
    }, [machineModelData]);

    // CORRECTED useEffect for price and cart filtering
    useEffect(() => {
        if (product && selectedSiteCurrency) {
            const key = `price_${selectedSiteCurrency}`;
            const currencyPrice = product[key];
            const finalPrice = product.purchasable ? currencyPrice : 0;
            setPrice(finalPrice);
        }

        // Re-filter recommendations when cart changes
        if (cartItems && recommendedProducts.length > 0) {
            const filtered = filterRecommendationsNotInCart(recommendedProducts, cartItems);
            // Only update if the filtered list is different
            if (filtered.length !== recommendedProducts.length) {
                setRecommendedProducts(filtered);
            }
        }
    }, [product?.product_id, selectedSiteCurrency, cartItems, recommendedProducts]); // Use cartItems instead of cart

    useEffect(() => {
        const fetchLists = async () => {
            try {
                const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
                const user_id = user.user_id;
                const response = await getRecommendationsByUserAndSite(user_id,site_id);
                console.log("In Single product getrecobyuserandsite response", response);
                if (response.status) {
                    let recommendations = [];

                    recommendations = response.data.filter((item) => item.user_id === user.user_id);

                    if (recommendations.length === 0) setSelectedList("new");
                    else setSelectedList(recommendations[0].id);
                    setLists(recommendations);
                }
            } catch (error) {
                console.error("Error fetching lists:", error);
            }
        };

        if (selectedSite) fetchLists();
    }, [selectedSite]);

    useEffect(() => {
        if (!dialogOpen) return;
        if (lists.length === 0) {
            setSelectedList("new");
        } else {
            setSelectedList(lists[0].id);
        }
        setNewListName("");
        setNewListDescription("");
        setNewListError("");
        setSelectListError("");
    }, [dialogOpen, lists]);

    // All your existing handler functions remain the same...
    const handleAddToRecommendationList = async () => {
        if (selectedList !== "new") {
            try {
                const response = await addProductsToRecommendationList(selectedList,[{ product_id: product.product_id, machine_id: machineID }]);
                console.log("In Single product addprodtorecolist response", response);
                if (response.status) {
                    handleCloseDialog();
                    if (response.data[0].status === "exists") {
                        Swal.fire({
                            icon: "info",
                            title: t('ALERT_MESSAGES.INFO'),
                            text: t('ALERT_MESSAGES.PART_ALREADY_PRESENT_LIST'),
                        });
                    } else {
                        Swal.fire({
                            icon: "success",
                            title: t('ALERT_MESSAGES.SUCCESS'),
                            text: t('ALERT_MESSAGES.PART_ADDED_TO_LIST'),
                        });
                    }
                } else {
                    console.error("Failed to add part to list");
                }
            } catch (error) {
                if (error.response.status === 409) {
                    setSelectListError(t('ALERT_MESSAGES.PART_ALREADY_ADDED_LIST'));
                }
                console.error("Error adding part to list:", error);
            }
        } else if (newListName) {
            setListCreationLoading(true); // Set loading true
            const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
            const user_id = user.user_id;

            try {
                const duplicateList = lists.some(
                    (list) =>
                        list.name.trim().toLowerCase() === newListName.trim().toLowerCase() &&
                        list.user_id === user_id &&
                        list.site_id === site_id
                );

                if (duplicateList) {
                    setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS')); // Use translation key
                    setListCreationLoading(false); // Reset loading
                    return;
                }
                const response = await createRecommendation({
                    name: newListName,
                    description: newListDescription || "",
                    user_id,
                    site_id,
                });
                console.log("In Single product create reco response", response);
                if (response.status) {
                    const newListId = response.data.id;
                    // Assuming addProductsToRecommendationList also returns a status or throws
                    const addProductResponse = await addProductsToRecommendationList(newListId,
    [{ product_id: product.product_id, machine_id: machineID }]);
                    console.log("In Single product addprodtorecolist response", addProductResponse);
                    if (addProductResponse.status) {
                        handleCloseDialog();
                        Swal.fire({
                            icon: "success",
                            title: t('ALERT_MESSAGES.SUCCESS'),
                            text: t('ALERT_MESSAGES.NEW_RECOMMENDATION_LIST'),
                        });
                    } else {
                        console.error("Failed to add product to the new list");
                        // If addProductResponse fails, you might want to delete the newly created list
                        // or at least inform the user better. For now, just error.
                    }
                } else {
                    // Handle backend error from createRecommendation
                    setNewListError(t('ALERT_MESSAGES.FAILED_TO_CREATE_THE_LIST'));
                    console.error("Failed to create the list");
                }
            } catch (error) {
                // Handle network errors or other unexpected errors
                if (error.response && error.response.status === 409) {
                    setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS'));
                } else {
                    setNewListError(t('ALERT_MESSAGES.ERROR_CREATING_LIST'));
                }
                console.error("Error creating new list:", error);
            } finally {
                setListCreationLoading(false); // Always reset loading
            }
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedList("");
        setNewListName("");
        setNewListDescription("");
        setNewListError("");
    };

    const handleListChange = (event) => {
        setSelectListError();
        setSelectedList(event.target.value);
    };

    const handleNewListNameChange = (event) => {
        setNewListName(event.target.value);
        setNewListError("");
    };

    const handleNewListDescriptionChange = (event) => {
        setNewListDescription(event.target.value);
    };

    const showModelError = () =>
        Swal.fire(t('ALERT_MESSAGES.ERROR'), t('GENERAL.FAILED_TO_OPEN_MODEL'), "error");

    const openUrlIfExists = async (url) => {
        if (!url) return false;
        const ok = await verifyResourceExists(url);
        if (!ok) return false;
        window.open(url);
        return true;
    };

    const build2dUrl = (pathValue) => {
        if (!pathValue) return "";
        let normalized = String(pathValue).trim();
        if (!normalized) return "";
        if (/^https?:\/\//i.test(normalized)) return normalized;
        normalized = normalized.replace(/\\/g, "/");
        normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
        normalized = normalized.replace(/^\/?stock-models\//i, "");
        normalized = normalized.replace(/^\/?models\//i, "");
        normalized = normalized.replace(/^\/?modelfiles\//i, "");
        normalized = normalized.replace(/^\/+/i, "");
        if (!/^2d\//i.test(normalized)) {
            normalized = `2D/${normalized}`;
        }
        if (!/\.pdf$/i.test(normalized)) {
            normalized = `${normalized}.pdf`;
        }
        return `${SERVER_BASE_URL}/stock-models/${normalized}`;
    };

    const handleModelOpen = async () => {
        if (!bestModel) return showModelError();

        setM3DButtonLoading(true);
        try {
            if (bestModel.type === "3D" && bestModel.format === "glb") {
                navigate(`/model-viewer/${machineID}/${extractFilename(bestModel.path)}`);
                return;
            }

            if (bestModel.type === "3D" && bestModel.format === "html") {
                const response = await processModelFile({
                    filename: extractFilename(bestModel.path),
                    machinename: machine_name,
                    currency: selectedSiteCurrency.toUpperCase(),
                    loggedinUser: user.user_id,
                    ecommachineid: machineID,
                    selectedSite,
                    machinenumber: machine_number,
                });
                if (response?.data?.url) {
                    await openUrlIfExists(SERVER_BASE_URL + response.data.url);
                } else {
                    showModelError();
                }
                return;
            }

            if (bestModel.type === "2D") {
                if (EXTERNAL_2D_ENABLED) {
                    const pdfUrl = build2dUrl(bestModel.path);
                    if (await openUrlIfExists(pdfUrl)) return;
                    showModelError();
                    return;
                }
                navigate(`/2d-model-viewer/${machineID}/${extractFilename(bestModel.path)}`);
            }
        } catch (err) {
            console.error("Error fetching the model:", err);
            showModelError();
        } finally {
            setM3DButtonLoading(false);
        }
    };

    const handleAddToRequest = async (event) => {
        event.stopPropagation();

        setIsLoading(true);
        setIsSuccess(false);
        try {
            const req_body = {
                machine_id: machineID,
                product_id: product.product_id,
                site_number: selectedSite,
                user_id: user.user_id,
            };

            try {
                const response = await saveRequestList(req_body);
                console.log("In Single product savereqlist response", response);
                if (response.status) {
                    setIsSuccess(true);
                    triggerToast(`Product ${product.product_name} added to Request List`, "success");
                    setTimeout(() => setIsSuccess(false), 1200);
                } else {
                    triggerToast(`Product ${product.product_name} ${t('GENERAL.ALREADY_IN_REQUEST_LIST')}`, "warning");
                }
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                console.error("Error fetching product name:", error);
            }
        } catch (error) {
            setIsSuccess(false);
            setIsLoading(false);
            triggerToast(`${t('GENERAL.UNABLE_TO_ADD_PARTS')} ${product?.product_name || "product"} to Request List`, "error");
        }
    };

    const handleSavedForLater = async (event) => {
        event.stopPropagation();

        setIsLoadingSFL(true);
        try {
            const req_body = {
                machine_id: machineID,
                product_id: product.product_id,
                site_number: selectedSite,
                user_id: user.user_id,
            };

            try {
                if(isSuccessSFL){
                    const response = await removeFromSavedForLater([product.product_id], selectedSite);
                if (response.status) {
                    setIsSuccessSFL(false);
                    triggerToast(`Product ${product.product_number} removed from Saved `, "success");
                } else {
                    triggerToast(`Product ${product.product_number} already in Saved for Later`, "warning");
                }
                setIsLoadingSFL(false);
                }else{
                const response = await saveProductForLater(req_body);
                if (response.status) {
                    setIsSuccessSFL(true);
                    triggerToast(`Product ${product.product_number} added to Saved for Later`, "success");
                } else {
                    triggerToast(`Product ${product.product_number} already in Saved for Later`, "warning");
                }
                setIsLoadingSFL(false);
                }
            } catch (error) {
                setIsLoadingSFL(false);
                console.error("Error fetching product name:", error);
            }
        } catch (error) {
            setIsSuccessSFL(false);
            setIsLoadingSFL(false);
            triggerToast(`${t('GENERAL.UNABLE_TO_SAVE')} ${product?.product_number || "product"} for later`, "error");
        }
    };

    const handleAddToCart = async (event) => {
        event.stopPropagation();

        setIsLoading(true);
        setIsSuccess(false);

        try {
            await addToCart({ ProductID: product.product_id, Quantity: quantity, Site: selectedSite });

            setIsSuccess(true);
            setIsLoading(false);
            triggerToast(`Product ${product.product_name} added to the cart`, "success");
            setTimeout(() => setIsSuccess(false), 2000);
        } catch (error) {
            setIsSuccess(false);
            setIsLoading(false);
            triggerToast(`Unable to add ${product?.product_name || "product"} to the cart`, "error");
        }
    };

      const handleQuantityChange = (event) => {
        const value = event.target.value;
       if (value === "") {
            setQuantity(1);
            return;
        }
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed)) {
            setQuantity(1);
            return;
        }
        const newQuantity = Math.min(MAX_QTY, Math.max(1, parsed));
        setQuantity(newQuantity);
    };
    const handleDocumentationNavigate = () => {
        if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
            navigate(ROUTES.DOCUMENT);
            return;
        }
        const username = selectedSiteData?.amf_method_username_ln;
        const password = selectedSiteData?.amf_method_password_ln;
        const loginUrl = buildExternalLoginUrl({
            templateUrl: DOCUMENT_EXTERNAL_URL,
            username,
            password,
        });
        if (!loginUrl) {
            navigate(ROUTES.DOCUMENT);
            return;
        }
        window.open(loginUrl, "_blank", "noopener,noreferrer");
    };

    useEffect(() => {
        let showAlert = false;
		let alertMessage = "";
        if (quantity > MAX_QTY) {
            	showAlert = true;
			alertMessage = t('ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX', { MAX_QTY });
            setQuantity(MAX_QTY);
        }
        if (quantity < 1) {
            showAlert = true;
			alertMessage = t('ALERT_MESSAGES.QUANTITY_CANNOT_BE_LESS_THAN_ONE');
            setQuantity(1);
        }
        if (showAlert) {
			triggerToast(alertMessage, "warning");
		}
    }, [quantity]);
    const handleOrdersView = () => {
        console.log("Viewing Order Details");
        navigate(`/order-history/${machineID}/${productID}`);
    };

    if (!machineID || !productID) {
        return <PageNotFound />;
    }

    const handleBomPathNavigate = (targetProductId) => {
        if (!targetProductId) return;
        navigate(`/machines/${machineID}/${targetProductId}`);
    };

    const containerClassNames = ["product-container"];
    if (isModalView) containerClassNames.push("product-container--modal");

    return (
        <Container className={containerClassNames.join(" ")} maxWidth="md">
            {loading && (
                <Box display="flex" justifyContent="center" className="loading-box">
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" className="error-message">
                    {error}
                </Alert>
            )}

            {!loading && !error && product && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box className="product-details">
                        <Box className="product-image-section">
                            <img
                                src={productImage}
                                alt={product.product_name || t('UI_TEXT.DEFAULT_PRODUCT')}
                                className="product-image-single"
                                onError={(e) => {
                                    e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
                                }}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    marginTop: 2,
                                    width: "100%",
                                }}>
                                {bestModel && (
                                    <Button
                                        startIcon={bestModel.type === "3D" ? <ViewInArIcon /> : <DescriptionIcon />}
                                        onClick={() => {
                                            if (hasMultipleModels) {
                                                setModelModalOpen(true);
                                                return;
                                            }
                                            handleModelOpen();
                                        }}
                                        disabled={m3DButtonLoading}
                                        aria-busy={m3DButtonLoading}
                                        sx={{ flex: 1, minHeight: 40 }}>
                                        {m3DButtonLoading ? (
                                            <>
                                                {t('UI_TEXT.LOADING')}
                                                <CircularProgress
                                                    color="inherit"
                                                    size={18}
                                                    sx={{ marginLeft: "6px" }}
                                                />
                                            </>
                                        ) : (
                                            bestModel.type === "3D"
                                                ? t('BUTTON.OPEN_3D_MODEL')
                                                : t('BUTTON.VIEW_2D_MODEL')
                                        )}
                                    </Button>
                                )}
                                <Button
                                    sx={{ flex: 1 }}
                                    startIcon={<DescriptionIcon />}
                                    onClick={() => handleDocumentationNavigate()}>
                                    {t('BUTTON.DOCUMETATION')}
                                </Button>
                            </Box>
                        </Box>
                        <Box className="product-info-section">
                            <Typography variant="h4" className="product-name">
                                {product.product_name}
                            </Typography>
                            <div className="header-divider"></div>
                            <Typography variant="body1" className="product-description">
                                <b>{formatInlineLabel(t('TABLE_HEADERS.MACHINE_NAME'))}</b> {machine_name} | <b>{formatInlineLabel(t('UI_TEXT.MACHINE'))}</b> {machine_number} | <b>{formatInlineLabel(t('UI_TEXT.PART'))}</b> {product.product_number}
                            </Typography>
                            {product.purchasable === 1 && price !== 0 && (
                                <>
                                    <Typography variant="h4" color="primary" className="product-price">
                                        {formatCurrency(price, selectedSiteCurrency)}
                                    </Typography>
                                    <Box className="product-quantity" marginY={2}>
                                        <Typography variant="body2" className="quantity-label">
                                            {t('UI_TEXT.QTY')}
                                        </Typography>
                                        <TextField
                                      type="number"
                                        variant="outlined"
                                        size="small"
                                        value={quantity}
                                        onChange={handleQuantityChange}
                                        onBlur={() => {
                                            if (quantity > MAX_QTY) setQuantity(MAX_QTY);
                                            if (quantity < 1) setQuantity(1);
                                        }}
                                        inputProps={{
                                            min: 1,
                                            max: MAX_QTY,
                                        }}
                                        className="quantity-input"
                                    />
                                    </Box>
                                </>
                            )}

                            {product.purchasable === 1 ? (
                                <>
                                    {orderCount > 0 ? (
                                        <>
                                            <Typography
                                                variant="body1"
                                                sx={{ fontStyle: "italic", fontSize: "12px" }}
                                                className="product-description">
                                                {UI_TEXT_HELPERS.getOrderCountText(orderCount)}
                                                <Tooltip title={t('UI_TEXT.VIEW_ALL_ORDERS_FOR_THIS_PART')}>
                                                    <Link
                                                        onClick={handleOrdersView}
                                                        sx={{ cursor: "pointer" }}
                                                        underline="hover">
                                                        {t('UI_TEXT.VIEW_ORDERS')}
                                                    </Link>
                                                </Tooltip>
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{ fontStyle: "italic", fontSize: "12px" }}
                                            className="product-description">
                                            {t('UI_TEXT.NO_ORDERS_FOR_THIS_PART')}
                                        </Typography>
                                    )}
                                    <Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "var(--primary-main)",
                                                padding: "8px 15px",
                                                fontWeight: "bolder",
                                                width: "fit-content",
                                                mt: 2,
                                            }}
                                            onClick={handleAddToCart}
                                            disabled={isLoading}
                                            className="button-primary">
                                            {isLoading ? (
                                                <>
                                                    <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.ADDING')}
                                                </>
                                            ) : isSuccess ? (
                                                <>
                                                    <CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.ADDED')}
                                                </>
                                            ) : (
                                                t('LABELS.ADD_TO_CART')
                                            )}
                                        </Button>

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                padding: "8px 15px",
                                                fontWeight: "bolder",
                                                mt: 2,
                                                ml: 2,
                                            }}
                                            onClick={handleSavedForLater}
                                            disabled={isLoadingSFL}
                                            startIcon={<BookmarkIcon />}
                                            className="button-outline">
                                            {isLoadingSFL ? (
                                                <>
                                                    <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                    {t('BUTTON.SAVING')}
                                                </>
                                            ) : isSuccessSFL ? (
                                                <>
                                                    {t('BUTTON.SAVED')}
                                                </>
                                            ) : (
                                                t('BUTTON.SAVE_FOR_LATER')
                                            )}
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "var(--primary-main)",
                                            padding: "8px 15px",
                                            fontWeight: "bolder",
                                            mt: 2,
                                        }}
                                        onClick={handleAddToRequest}
                                        disabled={isLoading}
                                        className="button-primary">
                                        {isLoading ? (
                                            <>
                                                <CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
                                                {t('BUTTON.ADDING')}
                                            </>
                                        ) : isSuccess ? (
                                            <>
                                                <CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
                                                {t('BUTTON.ADDED')}
                                            </>
                                        ) : (
                                            t('BUTTON.ADD_TO_REQUEST_LIST')
                                        )}
                                    </Button>
                                    <Typography variant="h6" color="error">
                                        {t('UI_TEXT.PRODUCT_UNAVAILABLE')}
                                    </Typography>
                                </>
                            )}

                            <Divider sx={{ mt: 4 }} />
                            <Typography variant="body1" className="product-description">
                                <b>{t('UI_TEXT.PART_TYPE')}</b>{" "}
                                {getRsplLabelForCode(product.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'))}{" "}
                                | <b>{t('UI_TEXT.WHERE_USED')}</b> {machine_name}{" "}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    marginTop: 2,
                                    width: "100%",
                                    columnGap: 2,
                                }}>
                                {canAddToList && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            padding: "8px 15px",
                                            fontWeight: "bolder",
                                            width: "fit-content",
                                        }}
                                        onClick={() => setDialogOpen(true)}>
                                        {t('BUTTON.ADD_TO_RECOMMENDATION')}
                                    </Button>
                                )}

                                <ModalWrapper
                                    open={dialogOpen}
                                    setOpen={setDialogOpen}
                                    onClose={handleCloseDialog}
                                    title={t('UI_TEXT.ADD_PRODUCT_TO_LIST')}
                                    content={
                                        <>
                                            {(selectListError || newListError) && (
                                                <Alert severity="error" sx={{ mb: 2 }}>
                                                    {selectListError || newListError}
                                                </Alert>
                                            )}
                                            <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
                                                <InputLabel id="select-list-label">{t('LABELS.SELECT_LIST')}</InputLabel>
                                                <Select
                                                    labelId="select-list-label"
                                                    value={selectedList}
                                                    onChange={handleListChange}
                                                    fullWidth
                                                    label={t('LABELS.SELECT_LIST')}
                                                    size="small"
                                                    error={!!selectListError}
                                                    helperText={selectListError}>
                                                    {lists.map((list) => (
                                                        <MenuItem key={list.id} value={list.id}>
                                                            {list.name}
                                                        </MenuItem>
                                                    ))}
                                                    {canCreateList && <MenuItem value="new">{t('MENU_ITEMS.CREATE_NEW_LIST')}</MenuItem>}
                                                </Select>
                                                {!!selectListError && <FormHelperText error>{selectListError}</FormHelperText>}
                                            </FormControl>
                                            {selectedList === "new" && (
                                                <>
                                                    <TextField
                                                        label={t('LABELS.LIST_NAME')}
                                                        value={newListName}
                                                        onChange={handleNewListNameChange}
                                                        fullWidth
                                                        size="small"
                                                        required
                                                        error={!!newListError}
                                                        helperText={newListError || t('UI_TEXT.ENTER_UNIQUE_NAME')}
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <TextField
                                                        label={t('LABELS.LIST_DESCRIPTION')}
                                                        value={newListDescription}
                                                        size="small"
                                                        onChange={handleNewListDescriptionChange}
                                                        fullWidth
                                                        sx={{ mb: 2 }}
                                                    />
                                                </>
                                            )}
                                            </>
                                        }
                                    actions={
                                        <>
                                            <Button onClick={handleCloseDialog} color="primary" variant="outlined">
                                                {t('BUTTON.CANCEL')}
                                            </Button>
                                            <Button
                                                onClick={handleAddToRecommendationList}
                                                color="primary"
                                                variant="contained"
                                                disabled={listCreationLoading || (!selectedList && selectedList !== 'new') || (selectedList === 'new' && !newListName)}>
                                                {listCreationLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Add"}
                                            </Button>
                                        </>
                                    }
                                />
                            </Box>


                        </Box>
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            mt: 1.5,
                            borderRadius: "10px",
                            // boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
                            overflow: "hidden", // Ensures children follow border radius
                        }}
                    >
                        <Divider sx={{ my: '10px' }} />
                        <Accordion
                            defaultExpanded
                            sx={{
                                boxShadow: "none",
                                borderRadius: 0,
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>{t("UI_TEXT.PART_DESCRIPTION")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary">
                                    {partDescription || t("UI_TEXT.NO_DESCRIPTION_AVAILABLE")}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded
                            sx={{
                                boxShadow: "none",
                                borderRadius: 0,
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>{t("UI_TEXT.BOM_TREE_TITLE")}</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t("UI_TEXT.BOM_PATH_HELPER")}
                                </Typography>

                                {bomPathError ? (
                                    <Alert severity="error">{t("ALERT_MESSAGES.FAILED_DATA")}</Alert>
                                ) : (
                                    <BomPathView
                                        tree={bomPathTree}
                                        isLoading={bomPathLoading}
                                        emptyLabel={t("UI_TEXT.NO_BOM_PATH_AVAILABLE")}
                                        onNavigateToProduct={handleBomPathNavigate}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>

                    
                    <Box sx={{ width: "100%", mt: 5 }}>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flexStart",
                            justifyContent: "flexStart"
                        }}>
                            <h4 style={{ marginBottom: '4px' }}>{t('UI_TEXT.FREQUENTLY_TOGETHER')}</h4>
                            <div style={{
                                borderBottom: '4px',
                                borderColor: 'var(--primary-main) !important',
                                background: 'var(--primary-main) !important',
                                backgroundColor: 'var(--primary-main) !important',
                                borderBottomColor: 'var(--primary-main) !important',
                                height: '4px',
                                margin: '2px 0',
                                width: '90px',
                            }}></div>
                        </div>

                        {recommendationsLoading && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={20} />
                                <Typography sx={{ ml: 1 }}>{t('UI_TEXT.LOADING_RECOMMENDATIONS')}</Typography>
                            </Box>
                        )}

                        {!recommendationsLoading && (
                            <Box>
                                {filterRecommendationsNotInCart(recommendedProducts, cartItems || []).length > 0 ? (
                                    filterRecommendationsNotInCart(recommendedProducts, cartItems || [])
                                        .map((product) => (
                                            <TopProductListItem
                                                key={product.product_id}
                                                product={product}
                                                machine_id={product.machine_id}
                                            />
                                        ))
                                ) : (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                        {t('UI_TEXT.NO_RECOMMENDATIONS')}
                                        </Alert>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            {hasMultipleModels && (
                <MultipleModelViewer
                    show={modelModalOpen}
                    setShow={setModelModalOpen}
                    machine_number={machine_number}
                    machine_name={machine_name}
                    productNumbers={product?.product_number ? [product.product_number] : []}
                    machine_id={machineID}
                    machine_product_name={product?.product_name}
                    modelData={machineModelData}
                />
            )}
        </Container>
    );
};

export default SingleProduct;
